/**
 * ``useProducers`` — TanStack Query hook around
 * ``GET /api/query/producers``.
 *
 * Stream A PR 5.2's data source for the Platforms-list panel
 * (Flow Q + Flow 05). Returns Producer **metadata** — display
 * name, organisation, classification, parent aggregator, onboarding
 * provenance, schema-version-bearing metadata blob. Live status /
 * position / message rates come from the snapshot + SSE endpoints;
 * :class:`PlatformsPanel` merges the two on ``producer_id`` to
 * render each row.
 *
 * # Why metadata-only
 *
 * ``control_plane.producers`` is the **registered-producer
 * inventory**; the fast-moving ``data.status_messages`` view changes
 * every second per producer. Server-side JOIN would couple two
 * indices that benefit from independent scaling. Client-side merge is
 * cheap (≤1500 rows at design ceiling per CLAUDE.md §1):
 * :class:`PlatformsPanel` joins this metadata with
 * :func:`useProducersStatus` on ``producer_id``.
 *
 * # Freshness — a polled global inventory
 *
 * The Platforms list is a **global** inventory of every registered
 * producer (Flow 05), independent of the map viewport. Producers
 * **auto-register on first sighting** (ADR-013 / ADR-014 amended
 * 2026-05-22) — a server-side event with no UI mutation — so the list
 * has to refresh itself to pick them up. It does so by **polling**
 * (``refetchInterval``), exactly like the per-row status query: a
 * single, predictable, viewport-independent cadence.
 *
 * This is deliberately NOT driven by the map's SSE firehose: that
 * stream is viewport-scoped, so it would surface only in-viewport
 * producers (the list would show "only a few") and per-event
 * invalidation would storm the query. Polling a global query is the
 * right tool for a global list.
 *
 * TanStack Query's default ``structuralSharing`` keeps ``data``
 * referentially stable when the producer set is unchanged, so a poll
 * that returns the same inventory causes no row re-render / no
 * scroll-anchor churn — the list re-renders only when a producer is
 * actually added or removed (see :class:`PlatformsPanel`'s memoised
 * rows). Stream D's onboarding mutations may additionally
 * ``invalidateQueries`` for an immediate refresh.
 *
 * Cross-references:
 *   - REQ-UI-007 (entity details list rendering).
 *   - REQ-UI-026 (TanStack Query for server state).
 *   - REQ-UI-051 (Platforms list as a selection-bus originator).
 *   - REQ-AUTH-024..027 (operator+ role gate; matches the snapshot
 *     endpoint's gate).
 *   - Flow Q §"Frame 1" / Flow 05 §"Frame 1" — per-row content
 *     schema the merge produces.
 *   - ADR-024 §2 (Platforms dockable surface).
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { Role } from "@/auth/session-types";
import type { components } from "@/lib/api-types.gen";

/**
 * The wire-shape response from ``GET /api/query/producers``,
 * re-exported from the generated OpenAPI types so consumers don't
 * have to dig into the components shape.
 */
export type ProducerListResponse = components["schemas"]["ProducerListResponse"];

/** One row of the Producers list. */
export type ProducerListItem = components["schemas"]["ProducerListItem"];

/**
 * Roles permitted to read ``/api/query/producers`` per
 * REQ-AUTH-024..027. Mirrors :const:`MAP_READER_ROLES` from
 * :file:`use-snapshot.ts` — the snapshot + producers endpoints
 * share the same RBAC gate (operator+).
 */
export const PRODUCERS_READER_ROLES: readonly Role[] = [
  "operator",
  "analyst",
  "admin",
  "support_engineer",
];

/**
 * Background poll cadence for the producers inventory.
 *
 * 5 s — fast enough that an auto-registered producer appears in the
 * list within a few seconds of first sighting (the map is instant via
 * SSE; the list is a polled global inventory), slow enough to be
 * negligible load (≤1500 rows). Structural sharing means an unchanged
 * inventory poll is a no-op for rendering.
 */
export const PRODUCERS_REFETCH_MS = 5_000;

/**
 * ``staleTime`` for the producers query — matched to the poll cadence
 * so incidental triggers (mount, window-focus) don't double up on the
 * interval's refetch.
 */
export const PRODUCERS_STALE_TIME_MS = PRODUCERS_REFETCH_MS;

/**
 * The TanStack Query key for the producers query. Exported for
 * test reach and for Stream D's invalidation calls.
 */
export function producersQueryKey(includeProviderNodes = false): readonly unknown[] {
  return ["producers", { includeProviderNodes }] as const;
}

/**
 * Options for {@link useProducers}.
 */
export interface UseProducersOptions {
  /**
   * Include rows the BFF classifies as a Provider's **own** node — an aggregator
   * that reported on its own behalf rather than a sensor platform.
   *
   * Default ``false``, which is what the Platforms list wants: an aggregator is not
   * a platform, and its information and controls live on the System display's
   * Provider surface (ADR-040 §D-1, storyboard Flow V).
   *
   * Set ``true`` only to resolve ``producer_id`` → display name
   * ({@link useProducerNames}). Records attributed to a provider node — chiefly
   * alerts ingested before alerts carried a per-sensor identity carrier — still
   * need a legible label rather than a raw UUID (REQ-UI-110).
   */
  readonly includeProviderNodes?: boolean;
}

/**
 * Poll cadence for the full inventory (``includeProviderNodes: true``).
 *
 * Deliberately slower than {@link PRODUCERS_REFETCH_MS}: this variant exists only to
 * resolve display names, which are slow-moving, and it is a **second** query key —
 * so polling it at the list's cadence would double the inventory load on the BFF for
 * information that does not change at that rate. A newly-sighted producer still
 * appears in the Platforms list within {@link PRODUCERS_REFETCH_MS}; only its label
 * on an Alerts / Detections row can lag, and that row falls back to the id
 * meanwhile.
 */
export const PRODUCERS_FULL_REFETCH_MS = 30_000;

/**
 * Type guard for the wire-shape response. Required because the
 * generated OpenAPI types describe the contract but don't ship
 * runtime validators — boundary-validation per CLAUDE.md §8.1
 * (silent failures forbidden).
 */
export function isProducerListResponse(value: unknown): value is ProducerListResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  if (!Array.isArray(obj.items)) return false;
  if (typeof obj.total !== "number") return false;
  if (typeof obj.duration_ms !== "number") return false;
  return obj.items.every(isProducerListItem);
}

function isProducerListItem(value: unknown): value is ProducerListItem {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.producer_id === "string" &&
    typeof obj.display_name === "string" &&
    typeof obj.organization === "string" &&
    typeof obj.classification === "string" &&
    // ``producer_type`` ('sapient') is a required field as
    // of the 2026-05-28 taxonomy decision (Flow 05 Frame 1 per-row
    // 'type' column). Validate presence + string-ness; the panel
    // narrows the literal at render. See the generated
    // ``ProducerListItem`` schema doc.
    typeof obj.producer_type === "string" &&
    (obj.aggregator_id === null || typeof obj.aggregator_id === "string") &&
    typeof obj.created_at === "string" &&
    typeof obj.created_by === "string" &&
    typeof obj.schema_version === "number" &&
    typeof obj.metadata === "object" &&
    obj.metadata !== null
  );
}

/** TanStack Query result alias for this hook. */
export type UseProducersResult = UseQueryResult<ProducerListResponse, ApiError>;

/**
 * Fetch the producers list.
 *
 * The query is disabled when the caller lacks an
 * operator+ role — the BFF would 403, but the upstream gate avoids
 * the noisy round-trip.
 *
 * @returns TanStack Query result whose ``data`` is the typed
 *     :type:`ProducerListResponse` when the fetch succeeds.
 *
 * Cross-references:
 *   - REQ-UI-007 / REQ-UI-026 / REQ-UI-051.
 *   - REQ-AUTH-024..027.
 *   - ADR-024 §2.
 */
export function useProducers(options: UseProducersOptions = {}): UseProducersResult {
  const session = useSession();
  const enabled = session.hasAnyRole(PRODUCERS_READER_ROLES);
  const includeProviderNodes = options.includeProviderNodes ?? false;
  // Two variants, two cache keys — sharing one would let whichever mounted first
  // decide whether provider nodes are in the list for everyone.
  const path = includeProviderNodes
    ? "/api/query/producers?include_provider_nodes=true"
    : "/api/query/producers";
  const refetchMs = includeProviderNodes ? PRODUCERS_FULL_REFETCH_MS : PRODUCERS_REFETCH_MS;

  return useQuery<ProducerListResponse, ApiError>({
    queryKey: producersQueryKey(includeProviderNodes),
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>(path, { signal });
      if (!isProducerListResponse(raw)) {
        // Boundary-validation failure — the BFF surface shifted.
        // Per CLAUDE.md §8.1 surface loudly.
        console.error("[meridian/platforms/use-producers] response failed shape validation", raw);
        throw new ApiError(500, raw, "producers response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    staleTime: refetchMs,
    // Poll the global inventory so auto-registered producers (no UI
    // mutation) appear without a viewport-scoped SSE bridge. Gated on
    // ``enabled`` so a logged-out / under-privileged session doesn't
    // poll. See the module docstring "Freshness" note.
    refetchInterval: enabled ? refetchMs : false,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
